import type { ResolvedVisualSettings } from '@/utils/pageHelpers/visualSettings';
import type { ModuleTypeName, ModuleTypes } from 'utils/modules';
import type { Colorways } from 'types/constants';
import type { SelectedPersonalization } from './types';

export interface PropInjectionContext {
  enrollmentUrl: string;
  visualSettings?: ResolvedVisualSettings;
}

export type RenderableModuleTypeName = Exclude<
  ModuleTypeName,
  'CalloutGridCard' | 'CanvasNavItem' | 'StatsigVariantContainer' | 'Tab'
>;

interface InjectedModuleProps {
  enrollmentUrl?: string;
  showFooterKeyline?: boolean;
  footerColor?: Colorways;
}

interface RenderableModuleMetadata {
  _exposure?: SelectedPersonalization;
}

export type RenderableModuleByTypeName<T extends RenderableModuleTypeName = RenderableModuleTypeName> =
  ModuleTypes[T] & { __typename: T } & RenderableModuleMetadata;

export type RenderableModule = {
  [K in RenderableModuleTypeName]: RenderableModuleByTypeName<K>;
}[RenderableModuleTypeName];

export type EnrichedModuleByTypeName<T extends RenderableModuleTypeName = RenderableModuleTypeName> =
  RenderableModuleByTypeName<T> & InjectedModuleProps;

/** Module enriched with injected per-typename props */
export type EnrichedModule = {
  [K in RenderableModuleTypeName]: EnrichedModuleByTypeName<K>;
}[RenderableModuleTypeName];

const ENROLLMENT_URL_TYPES = new Set<RenderableModuleTypeName>([
  'HeroConsolidated',
  'HomepageHero',
  'ImpactHeadline',
  'PageSection',
]);

const FOOTER_KEYLINE_TYPES = new Set<RenderableModuleTypeName>(['SectionFooter', 'SiteFooter']);

// Canvas Nav sits directly above the footer and reads as a continuation of it,
// so it inherits the page footer's resolved colorway.
const FOOTER_COLOR_TYPES = new Set<RenderableModuleTypeName>(['CanvasNav']);

/**
 * Enriches a module with per-typename props (enrollmentUrl, showFooterKeyline).
 * Pure function with zero experiment imports — mirrors the injection logic
 * from ModuleCollectionRenderer lines 232-252.
 */
export function injectModuleProps<T extends RenderableModule>(
  module: T,
  context: PropInjectionContext,
): T & InjectedModuleProps {
  const additional: InjectedModuleProps = {};

  if (ENROLLMENT_URL_TYPES.has(module.__typename)) {
    additional.enrollmentUrl = context.enrollmentUrl;
  }

  if (FOOTER_KEYLINE_TYPES.has(module.__typename) && context.visualSettings?.showFooterKeyline !== undefined) {
    additional.showFooterKeyline = context.visualSettings.showFooterKeyline ?? false;
  }

  if (FOOTER_COLOR_TYPES.has(module.__typename) && context.visualSettings?.footerColor) {
    additional.footerColor = context.visualSettings.footerColor;
  }

  if (Object.keys(additional).length === 0) return module;

  return { ...module, ...additional };
}
